import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TextField } from 'components/Fields';
import PasswordStrength, {
  validatePassword,
} from 'components/PasswordStrength';
import { useSearchParams, useSnack, useError } from 'hooks';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { PASSWORD_REGEX } from '../../constants';
import { activateAccount, getOauthInvitedUser } from 'api/auth';
import UserInfo from 'components/UserInfo';
import { useAsync } from 'react-async';
import { KoobAuthCard, KoobButton } from '@koob/ui';

const Signup = () => {
  const [{ invitation_token: invitationToken }] = useSearchParams();
  const { t } = useTranslation(['auth', 'errors']);
  const navigate = useNavigate();
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();

  const { data: response, error } = useAsync({
    promiseFn: getOauthInvitedUser,
    invitationToken,
  });

  const invitedUser = response?.data;
  const organizationDisplayName = invitedUser?.organizations?.[0]?.displayName;

  const handleSignup = async (values) => {
    try {
      const data = await activateAccount(values);

      if (data?.errors?.length > 0) {
        throw data?.errors?.[0];
      }

      notify(t('signup.succeeded'));
      navigate('/login');
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  return (
    <KoobAuthCard title={t('signup.title')} subtitle={t('signup.subtitle')}>
      {error ? (
        <div className="text-center text-gray-600">
          {t('signup.invalidInvitation')}
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            password: '',
            passwordConfirmation: '',
            invitationToken,
          }}
          validationSchema={Yup.object().shape({
            password: Yup.string()
              .required(t('errors:required'))
              .matches(PASSWORD_REGEX, t('errors:passwordInvalidCharacters')),
            passwordConfirmation: Yup.string()
              .oneOf([Yup.ref('password')], t('errors:passwordDoNotMatch'))
              .required(t('errors:required')),
          })}
          validate={validatePassword}
          onSubmit={handleSignup}
        >
          {({ isSubmitting, values, touched }) => (
            <Form>
              <UserInfo {...invitedUser} company={organizationDisplayName} />
              <div className="space-y-4">
                <div>
                  <TextField
                    type="password"
                    label={t('password')}
                    name="password"
                    autoComplete="off"
                  />
                  <PasswordStrength
                    password={values.password}
                    isTouched={touched.password}
                  />
                </div>
                <TextField
                  type="password"
                  label={t('passwordConfirmation')}
                  name="passwordConfirmation"
                />
              </div>

              <div className="mt-6">
                <KoobButton
                  type="submit"
                  variant="navy"
                  size="lg"
                  block
                  loading={isSubmitting}
                >
                  {t('signup.button')}
                </KoobButton>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </KoobAuthCard>
  );
};

export default Signup;
